import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView } from 'react-native';
import { X, Info } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

const TIER_EXPLANATIONS = [
  { name: 'Rookie', color: '#CD7F32', description: 'Entry rank. Baseline established.' },
  { name: 'Contender', color: '#C0C0C0', description: 'Consistent performance recorded.' },
  { name: 'Warrior', color: '#FFD700', description: 'Above-average output confirmed.' },
  { name: 'Champion', color: '#E5E4E2', description: 'Top-quartile standing.' },
  { name: 'Legend', color: '#B9F2FF', description: 'Elite percentile verified.' },
  { name: 'Master', color: '#FF1744', description: 'Near-apex performance.' },
  { name: 'Grandmaster', color: '#7C4DFF', description: 'Highest tier. Apex rank.' },
];

interface RankTierInfoProps {
  visible: boolean;
  onClose: () => void;
}

export default function RankTierInfo({ visible, onClose }: RankTierInfoProps) {
  const { theme } = useTheme();
  const c = theme.colors;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text }]}>Rank Tiers</Text>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <X size={20} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          <Text style={[styles.explanation, { color: c.textSecondary }]}>
            Ranks are percentile-based. Your tier reflects where you stand relative to all
            verified lifters — not cumulative points or activity volume. Logging more
            sessions does not inflate your rank; only verified strength output determines
            placement.
          </Text>

          <ScrollView style={styles.tierList} showsVerticalScrollIndicator={false}>
            {TIER_EXPLANATIONS.map((tier, i) => (
              <View key={tier.name} style={[styles.tierRow, i < TIER_EXPLANATIONS.length - 1 && { borderBottomWidth: 1, borderBottomColor: c.border }]}>
                <View style={[styles.tierDot, { backgroundColor: tier.color }]} />
                <View style={styles.tierContent}>
                  <Text style={[styles.tierName, { color: c.text }]}>{tier.name}</Text>
                  <Text style={[styles.tierDesc, { color: c.textSecondary }]}>{tier.description}</Text>
                </View>
              </View>
            ))}
          </ScrollView>

          <Text style={[styles.footnote, { color: c.textSecondary }]}>
            Tiers update when leaderboard rankings are recalculated. Freshness decay may
            affect your effective score over time.
          </Text>
        </View>
      </View>
    </Modal>
  );
}

/** Small inline button to trigger the tier info modal. */
export function RankTierInfoButton({ onPress }: { onPress: () => void }) {
  const { theme } = useTheme();
  return (
    <TouchableOpacity onPress={onPress} hitSlop={8} style={styles.infoButton}>
      <Info size={14} color={theme.colors.textSecondary} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 2,
    borderWidth: 1,
    padding: 20,
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
  },
  explanation: {
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 16,
  },
  tierList: {
    marginBottom: 12,
  },
  tierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
  },
  tierDot: {
    width: 10,
    height: 10,
    borderRadius: 4,
    marginRight: 12,
  },
  tierContent: {
    flex: 1,
  },
  tierName: {
    fontSize: 14,
    fontWeight: '600',
  },
  tierDesc: {
    fontSize: 12,
    marginTop: 2,
  },
  footnote: {
    fontSize: 11,
    lineHeight: 16,
    fontStyle: 'italic',
  },
  infoButton: {
    marginLeft: 6,
    padding: 2,
  },
});
